import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logout } from "../adminSlice";

// Profile photo when the backend sends one; otherwise (or if it fails to load) the
// user's initial on the brand navy.
function UserAvatar({ photo, name, className = "" }) {
    const initial = (name || "?").trim().charAt(0).toUpperCase();
    const [failed, setFailed] = useState(false);

    useEffect(() => setFailed(false), [photo]);

    if (photo && !failed) {
      return (
        <img
          src={photo}
          alt={name}
          onError={() => setFailed(true)}
          className={`w-10 h-10 rounded-full object-cover flex-shrink-0 ${className}`}
        />
      );
    }
    return (
      <div
        aria-hidden="true"
        className={`w-10 h-10 rounded-full bg-[#211b4f] flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ${className}`}
      >
        {initial}
      </div>
    );
}

/**
 * Header - Minimalist Admin Top Bar (Clean, Responsive)
 * User card (avatar + name + role) with logout popover lives here, on the right.
 */
const Header = ({ onMenuClick }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { name, role, photo } = useSelector((state) => state.admin);
    const [menuOpen, setMenuOpen] = useState(false);
    const popoverRef = useRef(null);

    // Close popover on outside click
    useEffect(() => {
      if (!menuOpen) return;
      const handler = (e) => {
        if (popoverRef.current && !popoverRef.current.contains(e.target)) {
          setMenuOpen(false);
        }
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }, [menuOpen]);

    const handleLogout = () => {
      setMenuOpen(false);
      dispatch(logout());
      navigate("/login");
    };

    const displayName = name || "Rize Admin";
    const displayRole = role || "Support Active";

    return (
        <header className="fixed top-0 right-0 left-0 lg:left-[16rem] h-16 flex items-center justify-between gap-2 px-3 sm:px-6 lg:px-8 bg-custom-pink border-b border-slate-200 z-50 shadow-sm">
          {/* Hamburger (mobile only) */}
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 text-slate-600 hover:text-slate-900 hover:bg-white/40 rounded-lg flex-shrink-0"
            aria-label="Open menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* Portal target — Onboarding tabs render here; scrollable on small screens */}
          <div
            id="admin-header-slot"
            className="flex-1 flex items-center justify-end overflow-x-auto scrollbar-hide px-2 sm:px-4"
          />

          {/* User card (right) with logout popover */}
          <div className="relative flex-shrink-0" ref={popoverRef}>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Open profile menu"
              className={`rounded-full transition-shadow ${
                menuOpen ? "ring-2 ring-slate-300" : "hover:ring-2 hover:ring-slate-200"
              }`}
            >
              <UserAvatar photo={photo} name={displayName} />
            </button>

            {/* Profile popover */}
            {menuOpen && (
              <div className="absolute top-full right-0 mt-2 w-60 bg-white border border-slate-200 rounded-2xl shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150">
                {/* Profile header */}
                <div className="flex items-center gap-3 px-4 py-3.5">
                  <UserAvatar photo={photo} name={displayName} />

                  <div className="min-w-0">
                    <p className="text-sm font-bold text-slate-800 truncate">{displayName}</p>
                    <p className="text-xs text-slate-500 truncate">{displayRole}</p>
                  </div>
                </div>

                <div className="border-t border-slate-100" />

                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-4 py-3 text-sm text-red-600 hover:bg-red-50 font-semibold transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Sign out
                </button>
              </div>
            )}
          </div>
        </header>
    )
}

export default Header;
